import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  Box,
  Tooltip,
  Typography,
  IconButton,
  InputLabel,
  SelectProps,
  InputAdornment,
  TextField as MuiTextField,
} from '@mui/material';

import { Icon } from '../icons';

import { PhoneInput } from './phone-input';
import { MaskedInput } from './masked-input';
import { InputProps, InputVariantClassnameMap } from './types';

const variantClassname: InputVariantClassnameMap = {
  outlined: '',
};

export const Input = ({
  mask,
  label,
  error,
  value,
  pattern,
  tooltip,
  onChange,
  required,
  slotProps,
  className,
  placeholder,
  type = 'text',
  shrink = true,
  variant = 'outlined',
  ...props
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const isPhone = type === 'tel';
  const isSelect = !!slotProps?.select;
  const isPassword = type === 'password';

  const adornment = isPassword ? (
    <InputAdornment position="end">
      <IconButton
        edge="end"
        size="small"
        onClick={() => setShowPassword((prev) => !prev)}
        className="text-graydark-primary p-0 hover:bg-transparent"
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        {showPassword ? (
          <Icon.EyeOff width={20} height={20} />
        ) : (
          <Icon.Eye width={20} height={20} />
        )}
      </IconButton>
    </InputAdornment>
  ) : null;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (pattern) {
      const regex = new RegExp(pattern, 'g');
      const value = event.target.value.replace(regex, '');
      event.target.value = value;
    }
    onChange?.(event);
  };

  const renderSelectValue = (selected: unknown) => {
    const placeholderComponent = (
      <Typography component="span" sx={{ color: 'text.disabled' }}>
        {placeholder || 'Select'}
      </Typography>
    );

    if (Array.isArray(selected)) {
      if (selected.length === 0) {
        return placeholderComponent;
      }
      return selected.join(', ');
    }

    if (typeof selected === 'string') {
      if (selected.length === 0 || selected === '') {
        return placeholderComponent;
      }
      return selected;
    }
    if (!selected || selected === '') {
      return placeholderComponent;
    }

    return selected as string;
  };

  const inputSlotProps: InputProps['slotProps'] = {
    inputLabel: { shrink },
    input: {
      endAdornment: adornment,
      ...(isPhone && {
        inputComponent: PhoneInput as any,
      }),
      ...(!!mask && {
        inputProps: { mask },
        inputComponent: MaskedInput as any,
      }),
      ...slotProps?.input,
    },
    ...slotProps,
    ...(slotProps?.select && {
      select: {
        ...slotProps.select,
        displayEmpty: true,
        IconComponent: Icon.ChevronDown,
        renderValue:
          (slotProps.select as SelectProps)?.renderValue ?? renderSelectValue,
      },
    }),
  };

  const isMultipleSelect = (inputSlotProps?.select as SelectProps)?.multiple;

  return (
    <Box>
      {label && (
        <InputLabel
          error={error}
          className="text-graydark-primary flex items-center text-xs leading-4.5"
        >
          <span className="min-w-0 truncate">{label}</span>
          {required && <span className="text-red-primary-primary"> *</span>}
          {tooltip && (
            <Tooltip title={tooltip}>
              <Icon.Info className="ml-1 inline-flex shrink-0" />
            </Tooltip>
          )}
        </InputLabel>
      )}
      <MuiTextField
        error={error}
        variant={variant}
        onChange={handleChange}
        slotProps={inputSlotProps}
        value={isMultipleSelect && !value ? [] : value}
        type={isPassword && showPassword ? 'text' : type}
        placeholder={!isSelect ? placeholder : undefined}
        className={twMerge(variantClassname[variant], className)}
        {...props}
      />
    </Box>
  );
};

export type { InputProps };
